import { useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { IconHome, IconLock, IconUserPlus } from '@tabler/icons-react';
import { PageHeader, useLayout } from '@hana/shell';
import { useT } from '@hana/i18n';
import './manage.i18n';
import { Button, Segmented, Skeleton, StateView, useToast } from '@hana/ui';
import { DEPT_ON_TIME, EXAM_RESULTS, MATRIX, ONBOARD, PEOPLE, REQ_COLS, avgTone, filterPeople, levelOf, passLevel, scoreTone, type By } from '../data/manage';
import { SearchRow } from '../courses/Courses';
import { useEdu } from '../useEdu';
import s from '../edu.module.css';

// Màn Quản lý đào tạo (board Mint-Training, app `mgr`; chỉ HR / trưởng bộ phận): Giao khoá · Hoàn thành · Kết quả thi.
// Nút chủ chốt "+ Giao khoá học" mở hộp giao khoá trên board — ở đây giả lập bằng toast (chưa ghi DB).

type Tab = 'assign' | 'done' | 'score';
const LEGEND: readonly [string, string][] = [['≥ 90%', 'done'], ['60–89%', 'mid'], ['< 60%', 'low'], ['Chưa giao', 'none']];
const GAUGE = 251.3;

function Assign() {
  const t = useT();
  const toast = useToast();
  const [by, setBy] = useState<By>('seat');
  return (
    <>
      <section className={`${s.card} ${s.mx}`} aria-label="Giao khoá theo ghế hoặc phòng">
        <div className={s.mxTop}>
          <Segmented kind="toggle" label="Xem theo" value={by} onChange={setBy} items={[{ key: 'seat', label: 'Theo ghế' }, { key: 'dept', label: 'Theo phòng' }]} />
          <span className={s.legend}>{LEGEND.map(([k, l]) => <span key={l}><i data-lv={l} />{k}</span>)}</span>
        </div>
        <div className={`${s.mxRow} ${s.mxHead}`}>
          <span>{by === 'seat' ? 'Ghế' : 'Phòng'}</span>
          {REQ_COLS.map((h) => <span key={h.k} title={h.full} className={s.mxC}>{h.k}</span>)}
        </div>
        {MATRIX[by].map((r) => (
          <div key={r.k} className={s.mxRow}>
            <div title={r.full} className={s.mxName}><b>{r.k}</b><span><svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" /></svg>{r.n}</span></div>
            {r.vals.map((v, j) => {
              const col = REQ_COLS[j]!;
              const aria = v === null ? `Giao "${col.k}" cho ${r.k}` : `${r.k} · ${col.k}: ${v}% hoàn thành · bấm để nhắc học`;
              return (
                <div key={col.k} className={s.mxC}>
                  <button type="button" className={s.cell} data-lv={v === null ? 'none' : levelOf(v)} aria-label={aria} title={aria}
                    onClick={() => toast({ msg: v === null ? `Giao "${col.k}" cho ${r.k} · ${r.n} người` : `Đã nhắc học "${col.k}" · ${r.k} · ${r.n} người` })}>
                    {v === null ? '+' : `${v}%`}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </section>
      <section className={`${s.card} ${s.onb}`} aria-label={t('mg.onb')}>
        <span className={s.onbIc} title={t('mg.onbTitle')}><IconUserPlus size={20} aria-hidden="true" /></span>
        <b>{t('mg.onb')}</b>
        {ONBOARD.map((o) => (
          <div key={o.k} className={s.onbItem}>
            <div><b>{o.k}</b><span>{o.p}%</span></div>
            <div className={s.bar}><i style={{ width: `${o.p}%` }} /></div>
            <small>{o.s}</small>
          </div>
        ))}
      </section>
    </>
  );
}

function Done() {
  const t = useT();
  const all = 0.86;
  const a = Math.PI * 0.9;
  const gx = 95 - 80 * Math.cos(a);
  const gy = 95 - 80 * Math.sin(a);
  return (
    <div className={s.twoCol}>
      <section className={`${s.card} ${s.rep}`}>
        <div className={s.repHead}><h2 className={s.h2}>{t('mg.onTime')}</h2><span>{t('mg.quarter', { q: '4/2026' })}</span></div>
        <div className={s.gauge}>
          <svg role="img" aria-label={t('mg.gaugeAria', { v: 86, g: 90 })} width="150" height="82" viewBox="0 0 190 104">
            <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--line)" strokeWidth="14" strokeLinecap="round" />
            <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--d-blue)" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${GAUGE} ${GAUGE}`} strokeDashoffset={GAUGE * (1 - all)} />
            <circle cx={gx.toFixed(1)} cy={gy.toFixed(1)} r="4" fill="var(--card)" stroke="var(--d-amber)" strokeWidth="2.5" />
            <text x="95" y="84" textAnchor="middle" fontSize="28" fontWeight="550" fill="var(--tx)" fontFamily="Inter, sans-serif">86%</text>
          </svg>
          <div><div>{t('mg.company')}</div><small><i className={s.goalDot} /> KPI 90%</small><small data-tone="warn">{t('mg.pts', { n: 4 })}</small></div>
        </div>
        {DEPT_ON_TIME.map((d) => (
          <div key={d.k} className={s.deptRow}>
            <span>{d.k}</span>
            <div className={s.track}><i data-lv={passLevel(d.w) === 'low' ? 'low' : 'mid'} style={{ width: `${d.w}%` }} /><em /></div>
            <b>{d.w}%</b>
          </div>
        ))}
      </section>
      <section className={`${s.card} ${s.tbl}`}>
        <div className={`${s.prow} ${s.pDone} ${s.phead}`}><span /><h2 className={s.h2}>{t('mg.byPerson')}</h2><span>{t('mg.coursesDone')}</span><span>{t('mg.late')}</span></div>
        {PEOPLE.map((p) => {
          const [x, y] = p.done.split('/').map(Number) as [number, number];
          return (
            <div key={p.k} className={`${s.prow} ${s.pDone}`}>
              <span className={s.av}>{p.ab}</span>
              <div className={s.pName}><b>{p.k}</b> <span>{p.seat}</span></div>
              <span className={s.pBar}><span role="img" aria-label={`Hoàn thành ${p.done}`} className={s.mini}><i data-lv={x === y ? 'done' : 'mid'} style={{ width: `${Math.round((x / y) * 100)}%` }} /></span>{p.done}</span>
              <span className={s.late} data-on={p.late ? '1' : undefined}>{p.late ? `${p.late} trễ` : '—'}</span>
            </div>
          );
        })}
      </section>
    </div>
  );
}

function Score({ q }: { q: string }) {
  const t = useT();
  const people = filterPeople(q);
  return (
    <div className={s.twoCol} data-start="1">
      <section className={`${s.card} ${s.tbl}`}>
        <div className={`${s.prow} ${s.pExam} ${s.phead}`}><h2 className={s.h2}>{t('mg.byExam')}</h2><span title={t('mg.passTitle')}>{t('mg.pass')}</span><span className={s.rEnd}>{t('mg.avg')}</span></div>
        {EXAM_RESULTS.map((e) => (
          <div key={e.k} className={`${s.prow} ${s.pExam} ${s.pTall}`}>
            <b title={e.full} className={s.ell}>{e.k}</b>
            <span className={s.pBar}><span className={s.track}><i data-lv={passLevel(e.pass)} style={{ width: `${e.pass}%` }} /><em /></span><b className={s.pct}>{e.pass}%</b></span>
            <b className={s.rEnd} data-tone={avgTone(e.avg)}>{e.avg}</b>
          </div>
        ))}
      </section>
      <section className={`${s.card} ${s.tbl}`}>
        <div className={`${s.prow} ${s.pScore} ${s.phead}`}><span /><h2 className={s.h2}>{t('mg.byPerson')}</h2><span title={t('mg.triesTitle')}>{t('mg.tries')}</span><span className={s.rEnd}>{t('mg.score')}</span></div>
        {people.map((p) => (
          <div key={p.k} className={`${s.prow} ${s.pScore}`}>
            <span className={s.av}>{p.ab}</span>
            <div className={s.pName}><b>{p.k}</b> <span>{p.seat}</span></div>
            <span>{p.tries} lần</span>
            <b className={s.rEnd} data-tone={scoreTone(p.score)}>{p.score} đ</b>
          </div>
        ))}
      </section>
    </div>
  );
}

function Locked() {
  const t = useT();
  const toast = useToast();
  return (
    <div className={s.page}>
      <div className={`${s.card} ${s.locked}`}>
        <span className={s.lockIc}><IconLock size={24} aria-hidden="true" /></span>
        <b>{t('mg.locked')}</b>
        <Button onClick={() => { location.assign('/edu'); toast({ msg: t('mg.home') }); }}><IconHome size={16} aria-hidden="true" />{t('mg.home')}</Button>
      </div>
    </div>
  );
}

export function ManageScreen() {
  const t = useT();
  const { learner, state } = useEdu();
  const phone = useLayout() === 'phone';
  const toast = useToast();
  // tab theo đường dẫn (/edu/manage · /done · /score) để mục QUẢN LÝ của menu sáng đúng chỗ
  const path = useRouterState({ select: (r) => r.location.pathname });
  const tab: Tab = /\/done\/?$/.test(path) ? 'done' : /\/score\/?$/.test(path) ? 'score' : 'assign';
  const nav = useNavigate();
  const setTab = (k: Tab) => void nav({ to: k === 'assign' ? '/edu/manage' : `/edu/manage/${k}` });
  const [q, setQ] = useState('');
  const ok = learner.manager && state === 'ready';
  const tabs = <Segmented label={t('mg.title')} value={tab} onChange={(k) => { setTab(k); setQ(''); }} items={[{ key: 'assign', label: t('mg.tAssign') }, { key: 'done', label: t('mg.tDone') }, { key: 'score', label: t('mg.tScore') }]} />;
  return (
    <>
      <PageHeader
        title={t('mg.title')}
        tabs={ok ? tabs : undefined}
        actions={ok ? <Button variant="primary" onClick={() => toast({ msg: t('mg.assignToast') })}>{t('mg.assignBtn')}</Button> : undefined}
      />
      {ok && phone && <div className={s.page}>{tabs}</div>}
      {ok && tab === 'score' && <div className={s.page}><SearchRow placeholder="Tìm người" q={q} onQ={setQ} sum={`${filterPeople(q).length} người`} /></div>}
      {state === 'ready' && !learner.manager ? <Locked /> : (
        <StateView
          state={state}
          title={state === 'empty' ? 'Chưa có dữ liệu đào tạo' : undefined}
          skeleton={<div className={s.skel}><Skeleton rows={6} /></div>}
        >
          <div className={s.page}>{tab === 'assign' ? <Assign /> : tab === 'done' ? <Done /> : <Score q={q} />}</div>
        </StateView>
      )}
    </>
  );
}
